import { isEmpty, mergeRight } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { createAction } from 'redux-actions';

import { replaceData, resetForm } from '@gef-ui/form-core/stream/transfer/control';

import { REQUEST_SIZE, RESULTS_LIMIT } from 'constants/general';
import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import { resetErrorsByPage } from 'features/common/submitFormDialog/slice';
// eslint-disable-next-line import/no-cycle
import taskForwarding from 'features/views/taskForwarding';
import { renameKeysByRelations } from 'features/views/taskOverview/utils';
import { trimFormData } from 'utils';

import * as types from './actionTypes';
import { dialogs, relations } from './constants';
import { TASK_SEARCH_FORM_NAME } from './form';
import { getLastSubmittedFormData, getSelectedTasks, getSort } from './selectors';
import { requestFactory } from './utils';

const resetState = createAction(types.RESET);
const setTasks = createAction(types.SET_TASKS, (tasks) => ({ tasks }));
const appendTasks = createAction(types.APPEND_TASKS, (tasks) => ({ tasks }));
const changeSort = createAction(types.CHANGE_SORT, (sort) => ({ sort }));
const saveLastSubmittedFormData = createAction(types.SAVE_LAST_SUBMITTED_FORMDATA, (formData) => ({ formData }));
export const selectTask = createAction(types.SELECT, (taskId) => ({ taskId }));
export const unselectTask = createAction(types.UNSELECT, (taskId) => ({ taskId }));

const handleResponse = (response, append, dispatch) => {
	if (!response || !response.body) {
		return;
	} else if (response.body.pageMetaData.totalRows > RESULTS_LIMIT) {
		dispatch(dialog.actions.open(dialogs.tooManyResults));
		dispatch(resetState());
	} else {
		dispatch(append ? appendTasks(response.body.data) : setTasks(response.body.data));
	}
};

export const loadMore = () => (dispatch, getState) => {
	const { queryParams, body } = fetch.selectors.getLastRequest(fetch.constants.urls.taskList)(getState());
	dispatch(
		fetch.actions.post(fetch.constants.urls.taskList, {
			queryParams: mergeRight(queryParams, { page: queryParams?.page + 1 }),
			body,
		}),
	).then((response) => handleResponse(response, true, dispatch));
};

export const loadSorted = (sort) => (dispatch, getState) => {
	const { queryParams, body } = fetch.selectors.getLastRequest(fetch.constants.urls.taskList)(getState());
	dispatch(changeSort(sort));
	dispatch(
		fetch.actions.post(fetch.constants.urls.taskList, {
			queryParams: mergeRight(queryParams, { sort }),
			body,
		}),
	).then((response) => handleResponse(response, false, dispatch));
};

export const paginationClick = (page) => (dispatch, getState) => {
	const lastRequest = fetch.selectors.getLastRequest(fetch.constants.urls.taskList)(getState());
	if (isEmpty(lastRequest)) {
		return;
	}

	const { queryParams, body } = lastRequest;
	dispatch(
		fetch.actions.post(fetch.constants.urls.taskList, {
			queryParams: mergeRight(queryParams, { page }),
			body,
		}),
	).then((response) => {
		handleResponse(response, false, dispatch);
	});
};

export const reload = () => async (dispatch, getState) => {
	const lastRequest = fetch.selectors.getLastRequest(fetch.constants.urls.taskList)(getState());
	const response = await dispatch(fetch.actions.post(fetch.constants.urls.taskList, lastRequest));

	handleResponse(response, false, dispatch);
};

export const prefillFormWithPreviouDataAndRefetch =
	() =>
	async (dispatch, getState, { sendControlNext }) => {
		const lastRequest = fetch.selectors.getLastRequest(fetch.constants.urls.taskList)(getState());

		if (!isNilOrEmpty(lastRequest)) {
			const formData = getLastSubmittedFormData(getState());
			sendControlNext(TASK_SEARCH_FORM_NAME, replaceData({ data: formData }));

			const response = await dispatch(fetch.actions.post(fetch.constants.urls.taskList, lastRequest));
			handleResponse(response, false, dispatch);
		}
	};

export const search =
	(page = 0, append = false) =>
	async (dispatch, getState, { getFormData }) => {
		const data = await getFormData(TASK_SEARCH_FORM_NAME);
		const formData = trimFormData(data);
		const sort = getSort(getState());

		dispatch(saveLastSubmittedFormData(formData));
		dispatch(resetErrorsByPage({ page: TASK_SEARCH_FORM_NAME }));
		dispatch(dialog.actions.clear());

		const response = await dispatch(
			fetch.actions.post(fetch.constants.urls.taskList, {
				takeLatest: true,
				queryParams: { size: REQUEST_SIZE, page, sort },
				body: requestFactory(renameKeysByRelations(relations, formData)),
			}),
		);

		handleResponse(response, append, dispatch);
	};

export const reset = () => (dispatch) => {
	dispatch(resetErrorsByPage({ page: TASK_SEARCH_FORM_NAME }));
	dispatch(dialog.actions.clear());
};

export const resetCriteria =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		dispatch(resetState());
		sendControlNext(TASK_SEARCH_FORM_NAME, resetForm());
		dispatch(resetErrorsByPage({ page: TASK_SEARCH_FORM_NAME }));
		dispatch(dialog.actions.clear());
		dispatch(fetch.actions.clearFetchStore(fetch.constants.urls.taskList));
	};

export const forwardTasks = () => (dispatch, getState) => {
	dispatch(taskForwarding.actions.setTasks(getSelectedTasks(getState())));
};
